import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { emReais } from '../../encomendas';
import {
  ROTULO_PAGAMENTO,
  dataHoraBr,
  lerMinhas,
  lerTodas,
  tempoParado,
  telefoneBr,
} from '../../encomendas-fluxo';
import type { ResumoDaEncomenda } from '../../encomendas-fluxo';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Detalhe } from './Detalhe';

/**
 * MINHAS ENCOMENDAS (balconista, fase 4) e TODAS AS ENCOMENDAS (admin, fase 9) — a mesma lista;
 * o servidor decide o que vem (`/minhas` só as dele). "Ação necessária" (pronta para o cliente)
 * sempre sobe primeiro; depois as atrasadas; depois as mais novas.
 */

export type FiltroDaLista =
  | 'todas'
  | 'precisam_de_mim'
  | 'andamento'
  | 'a_caminho'
  | 'prontas'
  | 'resolvidas'
  | 'canceladas'
  | 'atrasadas'
  // do painel do admin
  | 'sem_pedido'
  | 'chegaram'
  | 'pagas_atrasadas'
  | 'divergencia'
  | 'prazo_vencido'
  | 'urgentes'
  | 'entregas';

const FILTROS_DO_BALCONISTA: readonly [FiltroDaLista, string][] = [
  ['todas', 'Todas'],
  ['precisam_de_mim', 'Precisam de mim'],
  ['andamento', 'Em andamento'],
  ['a_caminho', 'A caminho'],
  ['prontas', 'Prontas'],
  ['resolvidas', 'Resolvidas'],
  ['canceladas', 'Canceladas'],
  ['atrasadas', 'Atrasadas'],
];
const FILTROS_DO_ADMIN: readonly [FiltroDaLista, string][] = [
  ...FILTROS_DO_BALCONISTA.filter(([k]) => k !== 'precisam_de_mim'),
  ['sem_pedido', 'Sem pedido'],
  ['chegaram', 'Chegaram'],
  ['divergencia', 'Divergência'],
  ['urgentes', 'Urgentes'],
  ['entregas', 'Entregas'],
  ['pagas_atrasadas', 'Pagas e atrasadas'],
  ['prazo_vencido', 'Prazo vencido'],
];

const ANDAMENTO = new Set(['CRIADA', 'PEDIDO_EM_ANDAMENTO', 'COMPRADA', 'FATURADA']);

function passa(e: ResumoDaEncomenda, f: FiltroDaLista): boolean {
  const aberta = e.resolucao === null;
  const m = e.situacao.macro;
  switch (f) {
    case 'todas':
      return true;
    case 'precisam_de_mim':
      return (
        aberta &&
        (e.situacao.acaoDoBalconista || e.sla.prazo === 'VENCIDO' || e.sla.prazo === 'PROXIMO')
      );
    case 'andamento':
      return aberta && ANDAMENTO.has(m);
    case 'a_caminho':
      return aberta && m === 'A_CAMINHO';
    case 'prontas':
      return aberta && m === 'PRONTA';
    case 'resolvidas':
      return m === 'CONCLUIDA';
    case 'canceladas':
      return m === 'CANCELADA';
    case 'atrasadas':
      return aberta && e.sla.nivel === 'ATRASADO';
    case 'sem_pedido':
      return aberta && (m === 'CRIADA' || m === 'PEDIDO_EM_ANDAMENTO');
    case 'chegaram':
      return aberta && m === 'RECEBIDA';
    case 'pagas_atrasadas':
      return (
        aberta && (e.pagamento === 'PAGO' || e.pagamento === 'SINAL') && e.sla.nivel === 'ATRASADO'
      );
    case 'divergencia':
      return aberta && e.divergenciaAberta;
    case 'prazo_vencido':
      return aberta && e.sla.prazo === 'VENCIDO';
    case 'urgentes':
      return aberta && e.urgente;
    case 'entregas':
      return aberta && e.modalidade === 'ENTREGA';
  }
}

const peso = (e: ResumoDaEncomenda) =>
  e.resolucao !== null ? 3 : e.situacao.acaoDoBalconista ? 0 : e.sla.nivel === 'ATRASADO' ? 1 : 2;

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function ListaDeEncomendas({
  sessao,
  modo,
  podeVincular,
  filtroInicial = 'todas',
}: {
  sessao: Sessao;
  modo: 'minhas' | 'todas';
  podeVincular: boolean;
  filtroInicial?: FiltroDaLista;
}) {
  const [lista, setLista] = useState<readonly ResumoDaEncomenda[] | null>(null);
  const [falha, setFalha] = useState(false);
  const [filtro, setFiltro] = useState<FiltroDaLista>(filtroInicial);
  const [busca, setBusca] = useState('');
  const [usuario, setUsuario] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [aberta, setAberta] = useState<string | null>(null);
  useEffect(() => setFiltro(filtroInicial), [filtroInicial]);

  const carregar = useCallback(() => {
    void (modo === 'minhas' ? lerMinhas() : lerTodas()).then((r) => {
      if (r.ok) {
        setLista(r.dados.encomendas);
        setFalha(false);
      } else setFalha(true);
    });
  }, [modo]);
  useEffect(() => {
    carregar();
    const t = window.setInterval(carregar, 20_000);
    return () => window.clearInterval(t);
  }, [carregar]);

  const contagem = useMemo(() => {
    const c = new Map<FiltroDaLista, number>();
    for (const [k] of modo === 'minhas' ? FILTROS_DO_BALCONISTA : FILTROS_DO_ADMIN)
      c.set(k, (lista ?? []).filter((e) => passa(e, k)).length);
    return c;
  }, [lista, modo]);

  const usuarios = useMemo(
    () => [...new Set((lista ?? []).map((e) => e.criadoPor.nome))].sort(),
    [lista],
  );
  const fornecedores = useMemo(
    () =>
      [
        ...new Set(
          (lista ?? [])
            .flatMap((e) => e.itens.flatMap((i) => [i.fornecedorConsultado, i.fornecedorComprado]))
            .filter((x): x is string => !!x),
        ),
      ].sort(),
    [lista],
  );

  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim());
    return (lista ?? [])
      .filter((e) => passa(e, filtro))
      .filter((e) => !usuario || e.criadoPor.nome === usuario)
      .filter(
        (e) =>
          !fornecedor ||
          e.itens.some(
            (i) => i.fornecedorConsultado === fornecedor || i.fornecedorComprado === fornecedor,
          ),
      )
      .filter((e) => {
        if (!q) return true;
        const alvo = semAcento(
          [
            e.codigo,
            e.cliente.nome,
            e.cliente.telefone,
            ...e.itens.flatMap((i) => [i.descricao, i.ean ?? '', i.codigoHos ?? '']),
            ...e.pedidos,
            ...e.notas,
          ].join(' '),
        );
        return (
          alvo.includes(q) ||
          (q.replace(/\D/g, '').length >= 4 && e.cliente.telefone.includes(q.replace(/\D/g, '')))
        );
      })
      .sort((a, b) => peso(a) - peso(b) || b.criadoEm.localeCompare(a.criadoEm));
  }, [lista, filtro, busca, usuario, fornecedor]);

  return (
    <div className="enc-lista">
      <div className="enc-lista-barra">
        <div className="enc-busca-campo enc-busca-curta">
          <Icone nome="search" tamanho={17} traco={1.7} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Cliente, telefone, produto, EAN, ENC-…"
            aria-label="Buscar encomenda"
          />
        </div>
        {modo === 'todas' && (
          <>
            <select
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              aria-label="Balconista"
              className="enc-select"
            >
              <option value="">Todos os balconistas</option>
              {usuarios.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
            <select
              value={fornecedor}
              onChange={(e) => setFornecedor(e.target.value)}
              aria-label="Fornecedor"
              className="enc-select"
            >
              <option value="">Todos os fornecedores</option>
              {fornecedores.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </>
        )}
      </div>
      <div className="enc-filtros" role="group" aria-label="Filtrar">
        {(modo === 'minhas' ? FILTROS_DO_BALCONISTA : FILTROS_DO_ADMIN).map(([k, rotulo]) => (
          <button
            key={k}
            type="button"
            className={filtro === k ? 'enc-filtro-sel' : ''}
            aria-pressed={filtro === k}
            onClick={() => setFiltro(k)}
          >
            {rotulo}
            {(contagem.get(k) ?? 0) > 0 && (
              <span className="enc-contador">{String(contagem.get(k))}</span>
            )}
          </button>
        ))}
      </div>

      {falha && <p className="enc-erro">Não foi possível carregar as encomendas.</p>}
      {lista === null && !falha && (
        <div className="enc-lista-esq">
          {[0, 1, 2].map((i) => (
            <div key={i} className="enc-cartao enc-esqueleto enc-linha-esq">
              <i />
              <i />
            </div>
          ))}
        </div>
      )}
      {lista !== null && visiveis.length === 0 && (
        <div className="enc-nada">
          <b>{lista.length === 0 ? 'Nenhuma encomenda ainda' : 'Nada neste filtro'}</b>
          <span>
            {lista.length === 0
              ? 'Use a Consulta para procurar o produto e clicar em Encomendar.'
              : 'Troque o filtro ou a busca.'}
          </span>
        </div>
      )}
      <ul className="enc-cartoes">
        {visiveis.map((e) => (
          <li key={e.id}>
            <CartaoDaEncomenda
              e={e}
              mostrarBalconista={modo === 'todas'}
              aoAbrir={() => setAberta(e.id)}
            />
          </li>
        ))}
      </ul>
      {aberta && (
        <Detalhe
          sessao={sessao}
          id={aberta}
          podeVincular={podeVincular}
          aoFechar={() => setAberta(null)}
          aoMudar={carregar}
        />
      )}
    </div>
  );
}

function CartaoDaEncomenda({
  e,
  mostrarBalconista,
  aoAbrir,
}: {
  e: ResumoDaEncomenda;
  mostrarBalconista: boolean;
  aoAbrir: () => void;
}) {
  const item = e.itens[0];
  const extra = e.itens.length > 1 ? ` + ${String(e.itens.length - 1)}` : '';
  const qtdFrase =
    item &&
    item.quantidade > 1 &&
    item.conferida < item.quantidade &&
    (item.chegou > 0 || item.faturada > 0)
      ? `${String(item.chegou > 0 ? item.chegou : item.faturada)}/${String(item.quantidade)} ${item.chegou > 0 ? 'recebidos' : 'faturados'}`
      : null;
  const aberta = e.resolucao === null;
  return (
    <button
      type="button"
      className={`enc-cartao enc-cartao-enc${e.situacao.acaoDoBalconista && aberta ? ' enc-cartao-acao' : ''}`}
      onClick={aoAbrir}
    >
      <FotoDoProduto url={e.foto} nome={item?.descricao ?? ''} tamanho={64} />
      <div className="enc-cartao-corpo">
        <div className="enc-cartao-topo">
          <span className="enc-codigo">{e.codigo}</span>
          {e.urgente && aberta && <span className="enc-tag enc-tag-urgente">Urgente</span>}
          {aberta && e.sla.nivel !== 'NORMAL' && (
            <span
              className={`enc-tag ${e.sla.nivel === 'ATRASADO' ? 'enc-tag-problema' : 'enc-tag-atencao'}`}
            >
              {e.sla.nivel === 'ATRASADO' ? 'Atrasada' : 'Atenção'} · parada há{' '}
              {tempoParado(e.sla.paradoMin)}
            </span>
          )}
          {aberta && e.sla.prazo === 'VENCIDO' && (
            <span className="enc-tag enc-tag-problema">Prazo vencido</span>
          )}
          {aberta && e.sla.prazo === 'PROXIMO' && (
            <span className="enc-tag enc-tag-atencao">Prazo chegando</span>
          )}
        </div>
        <h3 className="enc-cartao-nome">
          {item?.descricao ?? '—'}
          {extra} — {String(item?.quantidade ?? 0)} {item?.quantidade === 1 ? 'un' : 'un'}
        </h3>
        <p className="enc-cartao-sub">
          {e.cliente.nome} · {telefoneBr(e.cliente.telefone)} ·{' '}
          {e.modalidade === 'ENTREGA' ? 'Entrega' : 'Retirada'} ·{' '}
          {ROTULO_PAGAMENTO[e.pagamento] ?? e.pagamento}
          {e.valorTotalCombinado ? ` ${emReais(e.valorTotalCombinado)}` : ''}
        </p>
        <p className="enc-cartao-sub">
          Criada {dataHoraBr(e.criadoEm)}
          {mostrarBalconista ? ` por ${e.criadoPor.nome}` : ''}
          {e.pedidos.length ? ` · Pedido #${e.pedidos.join(', #')}` : ''}
          {e.notas.length ? ` · NF ${e.notas.join(', ')}` : ''}
          {mostrarBalconista && item?.fornecedorComprado
            ? ` · ${item.fornecedorComprado}`
            : mostrarBalconista && item?.fornecedorConsultado
              ? ` · sugerido ${item.fornecedorConsultado}`
              : ''}
        </p>
      </div>
      <div className="enc-cartao-status">
        <span className={`enc-macro enc-macro-${e.situacao.macro.toLowerCase()}`}>
          {e.situacao.rotulo}
        </span>
        <div className="enc-progresso" aria-label={`${String(e.situacao.progresso)}%`}>
          <i style={{ width: `${String(e.situacao.progresso)}%` }} />
        </div>
        <span className="enc-proximo-curto">
          {aberta ? (
            e.situacao.acaoDoBalconista ? (
              <b>AÇÃO NECESSÁRIA · {e.situacao.proximoPasso}</b>
            ) : (
              e.situacao.proximoPasso
            )
          ) : e.resolvidaEm ? (
            `Resolvida ${dataHoraBr(e.resolvidaEm)}`
          ) : (
            ''
          )}
          {qtdFrase ? ` · ${qtdFrase}` : ''}
        </span>
      </div>
    </button>
  );
}
